import { createSlice } from '@reduxjs/toolkit';
import { CreditLink, CreditEntry, CreditValidation } from 'entities/Credit';
import * as action from './credit.action';

interface State {
  linksAsGenerator: CreditLink[];
  linksAsReceiver: CreditLink[];
  entries: CreditEntry[];
  validation: CreditValidation[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: State = {
  linksAsGenerator: [],
  linksAsReceiver: [],
  entries: [],
  validation: [],
  status: 'idle',
  error: null,
};

const creditSlice = createSlice({
  name: 'credit',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    const pending = (state: State) => {
      state.status = 'loading';
      state.error = null;
    };
    const failed = (state: State, a: { payload: unknown }) => {
      state.status = 'failed';
      state.error =
        (a.payload as { message?: string })?.message ?? 'Erro desconhecido';
    };

    builder
      .addCase(action.getLinksByGenerator.pending, pending)
      .addCase(action.getLinksByGenerator.fulfilled, (state, a) => {
        state.status = 'succeeded';
        state.linksAsGenerator = a.payload.data;
      })
      .addCase(action.getLinksByGenerator.rejected, failed)

      .addCase(action.getLinksByReceiver.pending, pending)
      .addCase(action.getLinksByReceiver.fulfilled, (state, a) => {
        state.status = 'succeeded';
        state.linksAsReceiver = a.payload.data;
      })
      .addCase(action.getLinksByReceiver.rejected, failed)

      .addCase(action.createCreditLink.pending, pending)
      .addCase(action.createCreditLink.fulfilled, (state, a) => {
        state.status = 'succeeded';
        // Adiciona apenas como gerador (esta UC é a UG que distribui créditos)
        state.linksAsGenerator = [...state.linksAsGenerator, a.payload.data];
      })
      .addCase(action.createCreditLink.rejected, failed)

      .addCase(action.deleteCreditLink.fulfilled, (state, a) => {
        state.linksAsGenerator = state.linksAsGenerator.filter(
          (l) => l.id !== a.payload
        );
        state.linksAsReceiver = state.linksAsReceiver.filter(
          (l) => l.id !== a.payload
        );
      })

      .addCase(action.getCreditEntries.pending, pending)
      .addCase(action.getCreditEntries.fulfilled, (state, a) => {
        state.status = 'succeeded';
        state.entries = a.payload.data;
      })
      .addCase(action.getCreditEntries.rejected, failed)

      .addCase(action.createCreditEntry.fulfilled, (state, a) => {
        const newEntry = a.payload.data;
        const idx = state.entries.findIndex((e) => e.id === newEntry.id);
        if (idx >= 0) {
          state.entries[idx] = newEntry;
        } else {
          state.entries = [newEntry, ...state.entries];
        }
      })

      .addCase(action.deleteCreditEntry.fulfilled, (state, a) => {
        state.entries = state.entries.filter((e) => e.id !== a.payload);
      })

      .addCase(action.validateCredits.pending, pending)
      .addCase(action.validateCredits.fulfilled, (state, a) => {
        state.status = 'succeeded';
        state.validation = a.payload.data;
      })
      .addCase(action.validateCredits.rejected, failed);
  },
});

export const { reset: creditReset } = creditSlice.actions;
export default creditSlice.reducer;
